import { useIntl } from '@/intl';
import * as React from 'react';
import { useAlertService } from '@/react-pages/Prices/AlertService/hooks';
import {
  Button,
  Banner,
  FormField,
  FormFieldControl,
  FormFieldError,
  Icon,
  SpaceBetween,
  TextField,
  tk,
  Typography,
  UnavailableContentDialog,
} from '@sunwebgroup/ui-components';
import { styled } from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  background-color: ${tk('color_semantic_bg')};

  padding: 0 ${tk('spacing_md')} ${tk('spacing_md')} ${tk('spacing_md')};

  box-shadow:
    0px 0px 2px 0px rgba(0, 0, 0, 0.06),
    0px 1px 4px 0px rgba(0, 0, 0, 0.08),
    0px 2px 8px 0px rgba(0, 0, 0, 0.1);
`;

const Dialog = styled(UnavailableContentDialog)`
  padding: 0 !important;

  ${FormField}, ${Button} {
    width: 100%;
  }
`;

const Texts = styled(SpaceBetween)`
  flex: 1;
`;

export function AlertServiceSidebar() {
  const intl = useIntl();
  const [email, setEmail] = React.useState('');
  const [isValidEmail, setIsValidEmail] = React.useState(true);
  const { mutate, isLoading, isError, isSuccess, reset: resetMutationState } = useAlertService();

  const checkEmailValidity = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  const handleConfirm = () => {
    const isValid = checkEmailValidity(email);
    if (isValid) {
      resetMutationState();
      mutate(email);
    } else {
      setIsValidEmail(false);
    }
  };

  const shouldShowInvalidEmailMessage = isError || !isValidEmail;
  return (
    <Wrapper>
      <Dialog
        icon={<Icon glyph="IconBell" size="lg" />}
        title={intl.formatMessage({ id: 'prices_no_available_title' })}
        message={intl.formatMessage({ id: 'prices_no_available_message' })}
      >
        {isSuccess && (
          <Banner $type="success">
            <Texts direction="y">
              <Typography $variant="label">
                {intl.formatMessage({ id: 'price_alert_service-success-title' })}
              </Typography>
            </Texts>
          </Banner>
        )}
        {!isSuccess && (
          <>
            <FormField $error={Boolean(email) && !isValidEmail}>
              <FormFieldControl>
                <TextField.Root disabled={isLoading}>
                  <TextField.Content>
                    <TextField.Input type="text" onChange={handleOnChange} value={email} onKeyDown={handleKeyDown} />
                  </TextField.Content>
                </TextField.Root>
              </FormFieldControl>
              {shouldShowInvalidEmailMessage && (
                <FormFieldError>{intl.formatMessage({ id: 'price_alert_service-error' })}</FormFieldError>
              )}
            </FormField>
            <Button onClick={handleConfirm} disabled={isLoading}>
              {intl.formatMessage({ id: 'prices_no_available_button' })}
            </Button>
          </>
        )}
      </Dialog>
    </Wrapper>
  );
}
